import clsx from 'clsx';
import { kebabCase, toPairs } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import { AlertBannerContextProvider, CartContextProvider, Footer, Header, RedirectPage } from 'components';
import { useUser } from 'hooks';
import { AppRoutes, routes } from 'router';
import { appService, envService } from 'services';
import { resetFooterBanner, setAutolog } from 'store';

const App = () => {
  const showMaintenance = process.env.REACT_APP_SHOW_MAINTENANCE_PAGE === 'true';
  const isProduction = envService.isRunningInProd();

  const { isLoggedIn } = useUser();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const autolog = useSelector((state) => state.auth.autolog);
  const isSsoPage = useMemo(() => location.pathname.includes('sso'), [location.pathname]);
  const isAutologPage = useMemo(() => location.pathname.includes('autolog'), [location.pathname]);
  const showHeaderAndFooter = useMemo(() => {
    return !showMaintenance && !isSsoPage && !isAutologPage;
  }, [isAutologPage, isSsoPage, showMaintenance]);

  const containerClass = useMemo(() => {
    let currentRoute = '';
    /*
     * Add current route class to .app
     * containerClass is used to target specific pages in CSS
     */
    toPairs(routes).forEach(([routeName, routeData]) => {
      if (routeData?.path === location.pathname) currentRoute = routeName;
    });
    return currentRoute ? kebabCase(currentRoute) : '';
  }, [location.pathname]);

  const metaTags = useMemo(() => {
    let title = t(`meta.title.${appService.brand().toLowerCase()}`);
    switch (location.pathname) {
      /* TODO: adapt meta title and description for each page */
      default:
        if (!envService.isRunningInProd()) title = `[${process.env.REACT_APP_SENTRY_ENV}] - ${title}`;
        return { title, description: t('meta.description') };
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch(resetFooterBanner());
  }, [location.pathname]);

  useEffect(() => {
    // Display warning toast if user has logged in from autolog link
    if (autolog === 'success') {
      toast.warn(t('auth:autolog.message'), {
        position: 'bottom-right',
        autoClose: false,
        hideProgressBar: true,
        closeOnClick: false,
        closeButton: false,
        pauseOnHover: false,
        draggable: true
      });
    } else {
      if (autolog === 'failed' && isLoggedIn) {
        toast.error(t('auth:autolog.failed'));
        dispatch(setAutolog(''));
      } else if (!isLoggedIn) {
        toast?.dismiss();
      }
    }
  }, [autolog]);

  return (
    <div className={clsx('app', containerClass)}>
      <Helmet>
        <title>{metaTags.title}</title>
        <meta name="description" content={metaTags.description} />
        {!isProduction && <meta name="robots" content="noindex" />}
      </Helmet>

      {showHeaderAndFooter && !appService.isRedirection() && (
        <CartContextProvider>
          <Header />
        </CartContextProvider>
      )}

      <AlertBannerContextProvider>
        <main className={clsx(isLoggedIn && 'lg:mt-0')}>
          {appService.isRedirection() ? <RedirectPage /> : <AppRoutes />}
        </main>
      </AlertBannerContextProvider>

      {showHeaderAndFooter && <Footer />}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick={true}
        pauseOnHover={true}
        draggable={true}
        theme="light"
        icon={false}
      />
    </div>
  );
};

export default App;
